import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    getAuditOrgViewPermissions,
    getAuditTenantViewPermissions,
} from '@experiences/interfaces';
import type { PropsWithChildren } from 'react';
import React, {
    useContext,
    useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { isOrgAdminSelector } from '../../store/selectors';
import {
    useHasAnyPermissionAtScope,
    UserAuthPDPModelContext,
    UserAuthPDPStatusContext,
} from '../UserAuthPDPModelProvider/UserAuthPDPModelProvider';
import { CheckGuardComponent } from './CheckGuardComponent';
import { Forbidden403 } from './Forbidden403';

/**
 * Wraps the audit routes. Shows a 403 when the user holds none of:
 *  - org admin (short-circuit via {@link useHasAnyPermissionAtScope}),
 *  - `Audit.AuditLogs.View` on an org-scoped URL,
 *  - `Audit.TenantAuditLogs.View` for the tenant in the route, on a tenant-scoped URL.
 *
 * The two resource types are checked separately and never interchangeably. An `Org Auditor` holds
 * only the org action, so it cannot reach `/admin/tenant/:tenantId/audit`; a `Tenant Auditor` holds
 * only the tenant action, scoped to its own tenant, so it can reach neither the org page nor another
 * tenant's. That mirrors what the audit API enforces, so the UI cannot offer a page the API refuses.
 *
 * Gated on `EnableAuditorRoles`. The Auditor actions only exist where the Audit service's AuthZ
 * metadata has been deployed, so without the flag this must not run at all: it would deny anyone
 * who is not an org admin — a tenant admin included — purely because the action is absent.
 */
export const AuditRouteAccessGuard: React.FC<PropsWithChildren> = ({ children }) => {
    const isOrgAdmin = useSelector(isOrgAdminSelector);
    const pdpModel = useContext(UserAuthPDPModelContext);
    const {
        isLoading: isPdpLoading, hasError: pdpUnavailable,
    } = useContext(UserAuthPDPStatusContext);
    const { tenantId } = useParams<{ tenantId?: string }>();
    const EnableAuditorRoles = useFeatureFlagValue(Features.EnableAuditorRoles.name);

    const orgPermissions = useMemo(() => getAuditOrgViewPermissions(), []);
    const tenantPermissions = useMemo(() => getAuditTenantViewPermissions(), []);

    // Scope-correct: an organization grant covers a tenant, a tenant grant does not cover the
    // organization or another tenant, exactly as the policy resolves it.
    const hasAuditOrgPermission = useHasAnyPermissionAtScope(orgPermissions);
    const hasAuditTenantPermission = useHasAnyPermissionAtScope(tenantPermissions, tenantId);

    const gate = useMemo((): 'allow' | 'deny' | 'pending' => {
        // Flag off leaves these routes exactly as they behaved before the Auditor roles existed.
        // On a PDP outage, defer to the audit API, which enforces this independently — better than
        // blanking the page or showing a 403 we have no evidence for.
        if (!EnableAuditorRoles || isOrgAdmin || pdpUnavailable) {
            return 'allow';
        }

        if (isPdpLoading || pdpModel === undefined) {
            return 'pending';
        }

        return (tenantId ? hasAuditTenantPermission : hasAuditOrgPermission) ? 'allow' : 'deny';
    }, [
        EnableAuditorRoles, isOrgAdmin, pdpUnavailable, isPdpLoading, pdpModel,
        tenantId, hasAuditTenantPermission, hasAuditOrgPermission,
    ]);

    return (
        <CheckGuardComponent
            tester={gate === 'allow'}
            pending={gate === 'pending'}
            deny={<Forbidden403 data-cy="audit-route-forbidden" />}>
            {children}
        </CheckGuardComponent>
    );
};
